import React, { useCallback, useState } from 'react';
import * as Styled from './styled';
import Modal from 'ds/components/modal';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import { useLocale } from 'locale';
import CrossIcon from 'assets/svg/close-modal.svg';
import PhoneIcon from 'assets/svg/phone-1-5.svg';
import { createPhoneURI } from 'hooks/useDisplayPhone';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { ContactFormSubmitData, FormFields } from '../common/Fields';
import { connect } from 'react-redux';
import { setIsAgentOfficeContactPopupOpen, submitUserContactAgent } from 'store/state/app/actions';
import { isAgentOfficeContactPopupOpenSelector } from 'store/state/app/selectors';
import { RootAction, State } from 'store/state';
import { Button } from 'ds/components/button';
import { IContactInfo } from 'screens/AgentOfficePages/types';
import { IAgentPageData, IOfficePageData, User } from 'utils/entities';
import { userProfileSelector } from 'store/state/domainData/selectors';
import { NoImg } from 'screens/AgentOfficePages/common/components/NoImg';
import { Dispatch } from 'redux';
import { ContactAgentSubmitterSource, SubmitContactAgentPayload } from 'store/state/app/payloads';
import { routeSelector } from 'store/state/selectors/router';
import { Route } from 'config/routes';
import { AdditionalDataPageType, IContactPageSubmitVariables, LeadType } from 'store/sagas/apiService/types';
import withEvents from 'analytics/withEvents';
import { agentPageDataSelector } from 'store/state/domainData/selectors/agentById';
import { contactAgentPageResponseSelector } from 'store/state/mutationsResponse/selectors';
import { officePageDataSelector } from 'store/state/domainData/selectors/officeById';
import { SpacingBlock } from 'components/spacing-block';
import { LeadContactTermsAndConditions } from 'components/term-and-conditions/LeadContactTermsAndConditions';

interface ContactModalProps {
  isOpen: boolean;
  setContactOpen: (isOpen: boolean) => void;
  contactData?: IContactInfo;
  user: User;
  onContactPageSubmit: (payload: SubmitContactAgentPayload) => void;
  id: string;
  isAgent: boolean;
  isLeadingAgent?: boolean;
  isContacted: boolean;
  onPhoneFieldClick?: (props: ContactModalProps, isAgent: boolean) => void;
  onClose?: (props: ContactModalProps, isAgent: boolean) => void;
  onPhoneExpose?: (props: ContactModalProps, isAgent: boolean, isVisible: boolean) => void;
  agentPageInfo?: IAgentPageData;
  officePageInfo?: IOfficePageData;
}


const AgentOfficeContactModalBase: React.FC<ContactModalProps> = (props) => {
  const { t } = useLocale();
  const isDesktop = useScreenBreakpoint() >= 3;
  const [ phoneNumberIsVisible, setPhoneNumberIsVisible ] = useState(false);

  const {
    contactData: { phone, logo, name },
    isOpen,
    setContactOpen,
    user,
    id,
    onContactPageSubmit,
    isAgent,
    isContacted,
    onClose,
    onPhoneFieldClick,
    onPhoneExpose,
  } = props;

  const constructImageURI = useImageUrlBuilder();
  const imageURL = constructImageURI(logo);
  const isTermsAndConditionsVisible = !isContacted;

  const handleContactSubmit = useCallback((data: ContactFormSubmitData) => {
    onContactPageSubmit({
      key: id,
      variables: {
        id,
        email: data.userEmail,
        name: data.userName,
        phone: data.userPhone,
        type: isAgent ? LeadType.Agent : LeadType.Office,
        additionalData: {
          pageType: isAgent ? AdditionalDataPageType.NewAgentPage : AdditionalDataPageType.NewOfficePage,
        } as IContactPageSubmitVariables['additionalData'],
      },
      source: ContactAgentSubmitterSource.Modal,
    });
  }, []);

  const userName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
  const userPhone = user ? user.phone : '';
  const userEmail = user ? user.email : '';

  const handlePhoneSubmit = (e: React.MouseEvent<HTMLElement>) => {
    onPhoneExpose(props, isAgent, phoneNumberIsVisible);

    if (!phoneNumberIsVisible) {
      e.preventDefault();
      setPhoneNumberIsVisible(true);
    }
  };

  const handleClose = () => {
    setContactOpen(false);
    onClose(props, isAgent);
  };

  const handlePhoneClickEvent = () => {
    onPhoneFieldClick(props, isAgent);
  };

  if (!isOpen) return null;

  return isDesktop ? (
    <Modal
      isOpen
      title={<Styled.TitleWithClose>{t('developerPage.contactModal.title')}<CrossIcon onClick={handleClose} width={24} height={24} /></Styled.TitleWithClose>}
      hideCloseButton
      headerStyle={{
        margin: '-16px -32px',
        height: 'auto',
        fontSize: '15px',
        lineHeight: '24px',
        paddingTop: 0,
      }}
      footerStyle={{
        margin: '0 -32px -32px',
        borderTop: '1px solid #d3d3d3',
      }}
      onClose={handleClose}
      buttons={phone ? (
        <Styled.ModalButtonWrapper>
          <Styled.BottomText>{t('contactForm.contact.form.phoneButton.title')}</Styled.BottomText>
            <Styled.PhoneButton
              size="extraLarge"
              mode="ghost"
              href={createPhoneURI(phone)}
              onClick={handlePhoneSubmit}
            >
              <PhoneIcon />
              {phoneNumberIsVisible ? phone : t('contactAgentModal.phoneButton')}
            </Styled.PhoneButton>
        </Styled.ModalButtonWrapper>
      ) : null}
    >
      <Styled.SectionForm>
        <Styled.PopupTitleWrapper>
          <Styled.Logo>{imageURL ? <img src={imageURL} alt={name} /> : <NoImg size={44} />}</Styled.Logo>
          <Styled.FormTitle weight="bold">{name}</Styled.FormTitle>
        </Styled.PopupTitleWrapper>
        <FormFields
          onSubmit={handleContactSubmit}
          successfullySent={isContacted}
          withSubmitButton
          inModal
          userData={{ userPhone, userName, userEmail }}
          handlePhoneClickEvent={handlePhoneClickEvent}
        />
        {isTermsAndConditionsVisible ? (
          <SpacingBlock mTop={1}>
            <LeadContactTermsAndConditions />
          </SpacingBlock>
        ) : null}
      </Styled.SectionForm>
    </Modal>
  ) : (
    <Modal
      isOpen
      title={isContacted ? undefined : t('developerPage.contactModal.title')}
      footerStyle={isContacted ? {
        padding: '10px',
      } : {
        display: 'none',
      }}
      onClose={handleClose}
      mobileModalPositionBottom={isContacted}
      buttons={(
        <Styled.ModalCloseWrapper>
          <Button
            onClick={handleClose}
            fullWidth
            size="extraLarge"
          >
            {t('developerPage.contactModal.successClose')}
          </Button>
        </Styled.ModalCloseWrapper>
      )}
    >
      <Styled.SectionForm>
        {isContacted ? null : (
          <Styled.PopupTitleWrapper>
            <Styled.Logo>{imageURL ? <img src={imageURL} alt={name} /> : <NoImg size={44} />}</Styled.Logo>
            <Styled.FormTitle weight="bold">{name}</Styled.FormTitle>
          </Styled.PopupTitleWrapper>
        )}
        <FormFields
          onSubmit={handleContactSubmit}
          successfullySent={isContacted}
          withSubmitButton
          inModal
          userData={{ userPhone, userName, userEmail }}
          handlePhoneClickEvent={handlePhoneClickEvent}
        />
        {isTermsAndConditionsVisible ? (
          <SpacingBlock mTop={2}>
            <LeadContactTermsAndConditions />
          </SpacingBlock>
        ) : null}
      </Styled.SectionForm>
    </Modal>
  );
};

const AgentOfficeContactModalBaseWithEvents = withEvents<ContactModalProps>((sendEvent) => ({
  onPhoneFieldClick: (props: ContactModalProps, isAgent: boolean) => {
    sendEvent('user_contact_me_form_phone_text_box_click', 'user', {
      event: {
        agent_details: isAgent ? {
          id: props.id,
          name: props.agentPageInfo.name,
          office_id: props.agentPageInfo.officeId,
          office_name: props.officePageInfo.officeName,
          phone_number: props.contactData.phone,
        } : {
          id: props.id,
          office_id: props.officePageInfo.officeId,
          office_name: props.officePageInfo.officeName,
          phone_number: props.contactData.phone,
        },
        source: isAgent ? 'agent_page' : 'office_page',
      },
    });
  },
  onClose: (props: ContactModalProps, isAgent: boolean) => {
    sendEvent('user_contact_me_form_close', 'user', {
      event: {
        agent_details: isAgent ? {
          id: props.id,
          name: props.agentPageInfo.name,
          office_id: props.agentPageInfo.officeId,
          office_name: props.officePageInfo.officeName,
          phone_number: props.contactData.phone,
        } : {
          id: props.id,
          office_id: props.officePageInfo.officeId,
          office_name: props.officePageInfo.officeName,
          phone_number: props.contactData.phone,
        },
        source: isAgent ? 'agent_page' : 'office_page',
      },
    });
  },
  onPhoneExpose: (props: ContactModalProps, isAgent: boolean, isVisible: boolean) => {
    if (isVisible) {
      sendEvent('user_contact_me_call_submit', 'user', {
        property: {
          property_id: 'property_id',
          deal_type: 'for_sale',
          bulletin_info: {
            agent_details: (
              isAgent ? {
                id: props.id,
                name: props.agentPageInfo.name,
                office_id: props.agentPageInfo.officeId,
                office_name: props.officePageInfo.officeName,
                phone_number: props.contactData.phone,
                is_leading_agent: props.isLeadingAgent,
              } : {
                id: props.id,
                office_id: props.officePageInfo.officeId,
                office_name: props.officePageInfo.officeName,
                phone_number: props.contactData.phone,
              }
            ),
          },
        },
        event: {
          source: isAgent ? 'agent_page' : 'office_page',
        },
        rtbData: {
          phone: props.contactData.phone,
        },
      });
    }
    else {
      sendEvent('user_contact_me_phone_expose', 'user', {
        event: {
          agent_details: (
            isAgent ? {
              id: props.id,
              name: props.agentPageInfo.name,
              office_id: props.agentPageInfo.officeId,
              office_name: props.officePageInfo.officeName,
              phone_number: props.contactData.phone,
              is_leading_agent: props.isLeadingAgent,
            } : {
              id: props.id,
              office_id: props.officePageInfo.officeId,
              office_name: props.officePageInfo.officeName,
              phone_number: props.contactData.phone,
            }
          ),
          source: isAgent ? 'agent_page' : 'office_page',
        },
      });
    }
  },
}))(AgentOfficeContactModalBase);

const mapStateToProps = (state: State, props: { id: string }) => {
  const contactResponse = contactAgentPageResponseSelector(state, props.id);
  const isContacted = Boolean(contactResponse && contactResponse.data && contactResponse.data.contactV4);
  const isAgent = routeSelector(state).name === Route.AgentPage;
  return {
    isContacted,
    isOpen: isAgentOfficeContactPopupOpenSelector(state),
    user: userProfileSelector(state),
    isAgent,
    agentPageInfo: agentPageDataSelector(state),
    officePageInfo: officePageDataSelector(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => ({
  onContactPageSubmit: (payload: SubmitContactAgentPayload) => dispatch(submitUserContactAgent(payload)),
  setContactOpen: (payload: boolean) => dispatch(setIsAgentOfficeContactPopupOpen(payload)),
});

export const AgentOfficeContactModal = connect(mapStateToProps, mapDispatchToProps)(AgentOfficeContactModalBaseWithEvents);

